import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GetData from "../../../services/getData";
import { Button } from "../../../components/ui";
import "./PatientPlanPrint.css";
import "./PatientPlanPrintOtherFormat.css";
import "./PatientSmartDiagnosticsPrint.css";

export const SMART_DIAGNOSTICS_TEMPLATE_ID = 22;

export const SMART_DIAGNOSTICS_FORM_DEFAULT = {
  study_type: "",
  device: "",
  bp: "",
  hr: "",
  spo2: "",
  auscultation: "",
  vascular_age: "",
  stiffness_index: "",
  reflection_index: "",
  other_indicators: "",
  height: "",
  weight: "",
  bmi: "",
  waist: "",
  hand_right: "",
  hand_left: "",
  urine_protein: "",
  urine_glucose: "",
  urine_ketones: "",
  urine_ph: "",
  urine_blood: "",
  urine_other: "",
  saliva_ph: "",
  metabolic_note: "",
  t_score: "",
  z_score: "",
  bone_conclusion: "",
  vaginal_ph: "",
  women_conclusion: "",
};

const parseDocData = (raw) => {
  if (!raw) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return {};
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch (_) {
      return {};
    }
  }
  return {};
};

export const mapSmartDiagnosticsDocData = (raw) => ({
  ...SMART_DIAGNOSTICS_FORM_DEFAULT,
  ...parseDocData(raw),
});

export const findLatestSmartDiagnosticsDoc = (docs) => {
  const list = Array.isArray(docs) ? docs : [];
  return list
    .filter((doc) => Number(doc?.template_id) === SMART_DIAGNOSTICS_TEMPLATE_ID)
    .sort((a, b) => {
      const da = Date.parse(a?.created_date) || 0;
      const db = Date.parse(b?.created_date) || 0;
      if (db !== da) return db - da;
      return (Number(b?.id) || 0) - (Number(a?.id) || 0);
    })[0];
};

const formatDate = (dateString) => {
  if (
    !dateString ||
    dateString === "—" ||
    dateString === "0000-00-00 00:00" ||
    dateString === "0000-00-00 00:00:00"
  ) {
    return "";
  }
  const [datePart] = String(dateString).split(" ");
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return "";
  return `${day}.${month}.${year}`;
};

const displayValue = (value) => {
  if (value === 0) return "0";
  const text = value == null ? "" : String(value).trim();
  return text;
};

const PrintField = ({ label, value, suffix, full = false }) => (
  <div
    className={
      full
        ? "plan-print-other-field plan-print-other-field--full"
        : "plan-print-other-field"
    }
  >
    <span className="plan-print-other-field-label">{label}:</span>
    <span className="plan-print-other-field-value">
      {displayValue(value) || "\u00A0"}
    </span>
    {suffix ? (
      <span className="plan-print-other-field-suffix">{suffix}</span>
    ) : null}
  </div>
);

const GoldDivider = () => (
  <div className="plan-print-other-header-divider" aria-hidden="true">
    <span className="plan-print-other-header-divider-line" />
    <span className="plan-print-other-header-divider-diamond-wrap">
      <span className="plan-print-other-header-divider-diamond-outline" />
      <span className="plan-print-other-header-divider-diamond" />
    </span>
    <span className="plan-print-other-header-divider-line" />
  </div>
);

const PlanPrintOtherLogoIcon = () => (
  <svg
    className="plan-print-other-logo-icon"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M12 2.4C12 2.4 4.6 10.1 4.6 15.2a7.4 7.4 0 0 0 14.8 0C19.4 10.1 12 2.4 12 2.4z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M12 5.8C12 5.8 7.2 11.2 7.2 14.9a4.8 4.8 0 0 0 9.6 0C16.8 11.2 12 5.8 12 5.8z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M12 9.2C12 9.2 9.4 12.4 9.4 14.7a2.6 2.6 0 0 0 5.2 0C14.6 12.4 12 9.2 12 9.2z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

const HeartIcon = () => (
  <svg className="plan-print-other-section-rail-icon" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 20.2S4.8 15.4 4.8 9.8A4.2 4.2 0 0 1 12 7.4a4.2 4.2 0 0 1 7.2 2.4c0 5.6-7.2 10.4-7.2 10.4z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path
      d="M5 12.2h2.4l1.5-2.4 2.2 5 1.7-2.6H19"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BodyIcon = () => (
  <svg className="plan-print-other-section-rail-icon" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="4.6" r="2.1" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M7.2 10.4L4.8 13.2M16.8 10.4L19.2 13.2"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M8.2 9.4c1.1 1.3 2.3 2 3.8 2s2.7-.7 3.8-2"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M10.4 11.6L9.4 20.4M13.6 11.6L14.6 20.4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const TubeIcon = () => (
  <svg className="plan-print-other-section-rail-icon" viewBox="0 0 24 24" fill="none">
    <path
      d="M9.2 3.4h5.6M10.2 3.4v5.2L6.8 16.6A3.5 3.5 0 0 0 10 21h4a3.5 3.5 0 0 0 3.2-4.4L13.8 8.6V3.4"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.4 14.4h7.2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const BoneIcon = () => (
  <svg className="plan-print-other-section-rail-icon" viewBox="0 0 24 24" fill="none">
    <path
      d="M6.2 8.2a2.2 2.2 0 1 1 3.1-3.1L17.8 13.6a2.2 2.2 0 1 1-3.1 3.1L6.2 8.2z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path
      d="M7.4 6.2a2.2 2.2 0 1 1-3.1 3.1M16.6 17.8a2.2 2.2 0 1 0 3.1-3.1"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
);

const VenusIcon = () => (
  <svg className="plan-print-other-section-rail-icon" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="9" r="5.2" stroke="currentColor" strokeWidth="1.7" />
    <path
      d="M12 14.2v6.4M9.2 17.6h5.6"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
);

const DoctorIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="7.2" r="3.1" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M5.4 19.4c1-3.4 3.2-5.1 6.6-5.1s5.6 1.7 6.6 5.1"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const InfoClipboardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect
      x="6"
      y="5.2"
      width="12"
      height="14.6"
      rx="1.8"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path d="M9.2 4.4h5.6v2.4H9.2V4.4z" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="12" cy="12.2" r="0.9" fill="currentColor" />
    <path
      d="M12 14.2v3.2"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const PrintRail = ({ number, icon }) => (
  <div className="plan-print-other-section-rail" aria-hidden="true">
    {icon}
    <div className="plan-print-other-section-rail-number">{number}</div>
  </div>
);

const SectionTitle = ({ number, en, ru }) => (
  <>
    <div className="plan-print-other-section-title">
      {number}. {en}
    </div>
    {ru ? (
      <div className="plan-print-other-diagnostics-heading">{ru}</div>
    ) : null}
  </>
);

export function SmartDiagnosticsPrintSheet({
  formData: formDataProp,
  appointments,
  docMeta,
}) {
  const { t } = useTranslation("patient");
  const formData = {
    ...SMART_DIAGNOSTICS_FORM_DEFAULT,
    ...(formDataProp || {}),
  };
  const patientFio = appointments?.result?.fio?.value || "";
  const doctorFio = appointments?.result?.doc_name?.value || "";
  const birthDate = formatDate(appointments?.result?.birthDate?.value || "");
  const examDate = formatDate(docMeta?.created_date || docMeta?.date || "");
  const examNumber = docMeta?.id ? String(docMeta.id) : "";

  return (
    <>
      <header className="plan-print-other-header">
        <div className="plan-print-other-header-row">
          <div className="plan-print-other-header-brand">
            <PlanPrintOtherLogoIcon />
            <div className="plan-print-other-brand-text">
              <div className="plan-print-other-brand-name">REFRESH</div>
              <div className="plan-print-other-brand-sub">LONGEVITY CLINIC</div>
            </div>
          </div>
          <div className="plan-print-other-header-title-wrap">
            <div className="plan-print-other-header-title">
              SMART DIAGNOSTICS
              <br />
              {t("psd.print.results")}
            </div>
            <GoldDivider />
          </div>
        </div>

        <div className="plan-print-other-patient-meta">
          <div className="plan-print-other-patient-meta-row">
            <PrintField label={t("usi.patientFio")} value={patientFio} full />
          </div>
          <div className="plan-print-other-patient-meta-row plan-print-other-patient-meta-row--triple">
            <PrintField label={t("usi.birthDate")} value={birthDate} />
            <PrintField label={t("usi.studyDate")} value={examDate} />
            <PrintField label={t("usi.examNumber")} value={examNumber} />
          </div>
          <div className="plan-print-other-patient-meta-row plan-print-other-patient-meta-row--quad">
            <PrintField
              label={t("psd.fields.height")}
              value={formData.height}
              suffix={t("psd.units.cm")}
            />
            <PrintField
              label={t("psd.fields.weight")}
              value={formData.weight}
              suffix={t("psd.units.kg")}
            />
            <PrintField
              label={t("psd.fields.bmi")}
              value={formData.bmi}
            />
            <PrintField
              label={t("psd.fields.waist")}
              value={formData.waist}
              suffix={t("psd.units.cm")}
            />
          </div>
          <div className="plan-print-other-patient-meta-row">
            <PrintField
              label={t("psd.fields.studyType")}
              value={formData.study_type}
              full
            />
          </div>
          <div className="plan-print-other-patient-meta-row plan-print-other-patient-meta-row--split">
            <PrintField label={t("psd.fields.device")} value={formData.device} />
            <PrintField label={t("usi.doctor")} value={doctorFio} />
          </div>
        </div>
      </header>

      <section className="plan-print-other-section">
        <PrintRail number="1" icon={<HeartIcon />} />
        <div className="plan-print-other-section-body">
          <SectionTitle number="1" en="HEART & VASCULAR" />
          <div className="plan-print-other-diagnostics">
            <div className="plan-print-other-diagnostics-col">
              <div className="plan-print-other-diagnostics-heading">
                {t("psd.blocks.heartVessels")}
              </div>
              <PrintField
                label={t("psd.fields.bp")}
                value={formData.bp}
                suffix={t("psd.units.mmHg")}
              />
              <PrintField
                label={t("psd.fields.hr")}
                value={formData.hr}
                suffix={t("psd.units.bpm")}
              />
              <PrintField
                label={t("psd.fields.spo2")}
                value={formData.spo2}
                suffix={t("psd.units.percent")}
              />
              <PrintField
                label={t("psd.fields.auscultation")}
                value={formData.auscultation}
              />
            </div>
            <div className="plan-print-other-diagnostics-col">
              <div className="plan-print-other-diagnostics-heading">
                {t("psd.blocks.angioscan")}
              </div>
              <PrintField
                label={t("psd.fields.vascularAge")}
                value={formData.vascular_age}
                suffix={t("psd.units.years")}
              />
              <PrintField
                label={t("psd.fields.stiffnessIndex")}
                value={formData.stiffness_index}
              />
              <PrintField
                label={t("psd.fields.reflectionIndex")}
                value={formData.reflection_index}
              />
              <PrintField
                label={t("psd.fields.otherIndicators")}
                value={formData.other_indicators}
                full
              />
            </div>
          </div>
        </div>
      </section>

      <section className="plan-print-other-section">
        <PrintRail number="2" icon={<BodyIcon />} />
        <div className="plan-print-other-section-body">
          <SectionTitle number="2" en="BODY & STRENGTH" />
          <div className="plan-print-other-diagnostics">
            <div className="plan-print-other-diagnostics-col">
              <div className="plan-print-other-diagnostics-heading">
                {t("psd.blocks.bodyMuscle")}
              </div>
              <PrintField
                label={t("psd.fields.height")}
                value={formData.height}
                suffix={t("psd.units.cm")}
              />
              <PrintField
                label={t("psd.fields.weight")}
                value={formData.weight}
                suffix={t("psd.units.kg")}
              />
              <PrintField
                label={t("psd.fields.bmi")}
                value={formData.bmi}
              />
              <PrintField
                label={t("psd.fields.waist")}
                value={formData.waist}
                suffix={t("psd.units.cm")}
              />
            </div>
            <div className="plan-print-other-diagnostics-col">
              <div className="plan-print-other-diagnostics-heading">
                {t("psd.blocks.dynamometry")}
              </div>
              <PrintField
                label={t("psd.fields.handRight")}
                value={formData.hand_right}
                suffix={t("psd.units.kg")}
              />
              <PrintField
                label={t("psd.fields.handLeft")}
                value={formData.hand_left}
                suffix={t("psd.units.kg")}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="plan-print-other-section">
        <PrintRail number="3" icon={<TubeIcon />} />
        <div className="plan-print-other-section-body">
          <SectionTitle
            number="3"
            en="METABOLIC SCREEN"
            ru={t("psd.blocks.metabolic")}
          />
          <div className="plan-print-other-diagnostics">
            <div className="plan-print-other-diagnostics-col">
              <div className="plan-print-other-diagnostics-heading">
                {t("psd.blocks.urineExpress")}:
              </div>
              <div className="psd-print-urine-grid">
                <PrintField
                  label={t("psd.fields.urineProtein")}
                  value={formData.urine_protein}
                />
                <PrintField
                  label={t("psd.fields.urinePh")}
                  value={formData.urine_ph}
                />
                <PrintField
                  label={t("psd.fields.urineGlucose")}
                  value={formData.urine_glucose}
                />
                <PrintField
                  label={t("psd.fields.urineBlood")}
                  value={formData.urine_blood}
                />
                <PrintField
                  label={t("psd.fields.urineKetones")}
                  value={formData.urine_ketones}
                />
                <PrintField
                  label={t("psd.fields.urineOther")}
                  value={formData.urine_other}
                />
              </div>
            </div>
            <div className="plan-print-other-diagnostics-col">
              <div className="plan-print-other-diagnostics-heading">
                {t("psd.blocks.salivaPh")}
              </div>
              <PrintField
                label={t("psd.fields.salivaPh")}
                value={formData.saliva_ph}
              />
              <PrintField
                label={t("psd.fields.metabolicNote")}
                value={formData.metabolic_note}
                full
              />
              <div className="plan-print-other-section-line" aria-hidden="true" />
              <div className="plan-print-other-section-line" aria-hidden="true" />
            </div>
          </div>
        </div>
      </section>

      <section className="plan-print-other-section">
        <PrintRail number="4" icon={<BoneIcon />} />
        <div className="plan-print-other-section-body">
          <SectionTitle
            number="4"
            en="BONE HEALTH"
            ru={t("psd.blocks.bone")}
          />
          <div className="psd-print-densitometry-row">
            <span className="psd-print-densitometry-label">
              {t("psd.blocks.densitometry")}:
            </span>
            <PrintField
              label={t("psd.fields.tScore")}
              value={formData.t_score}
            />
            <PrintField
              label={t("psd.fields.zScore")}
              value={formData.z_score}
            />
          </div>
          <PrintField
            label={t("psd.fields.boneConclusion")}
            value={formData.bone_conclusion}
            full
          />
        </div>
      </section>

      <section className="plan-print-other-section">
        <PrintRail number="5" icon={<VenusIcon />} />
        <div className="plan-print-other-section-body">
          <SectionTitle
            number="5"
            en="WOMEN'S HEALTH"
            ru={t("psd.blocks.byIndication")}
          />
          <PrintField
            label={t("psd.fields.vaginalPh")}
            value={formData.vaginal_ph}
            full
          />
          <PrintField
            label={t("psd.fields.womenConclusion")}
            value={formData.women_conclusion}
            full
          />
        </div>
      </section>

      <div className="psd-print-footer">
        <div className="psd-print-cards">
          <div className="psd-print-doctor">
            <span className="psd-print-card-icon" aria-hidden="true">
              <DoctorIcon />
            </span>
            <div className="psd-print-doctor-content">
              <div className="psd-print-card-title">{t("psd.print.doctor")}</div>
              <div className="psd-print-doctor-signature">
                <div className="psd-print-sign-field psd-print-sign-field--fio">
                  <span className="psd-print-sign-line">
                    {doctorFio || "\u00A0"}
                  </span>
                  <span className="psd-print-sign-hint">
                    {t("usi.signatureFio")}
                  </span>
                </div>
                <span className="psd-print-sign-sep">/</span>
                <div className="psd-print-sign-field psd-print-sign-field--mark">
                  <span className="psd-print-sign-line">&nbsp;</span>
                  <span className="psd-print-sign-hint">
                    {t("usi.signatureSign")}
                  </span>
                </div>
              </div>
              <div className="psd-print-doctor-date">
                <span className="psd-print-sign-label">
                  {t("usi.dateLabel")}
                </span>
                <span className="psd-print-sign-line psd-print-sign-line--date">
                  {examDate || "\u00A0"}
                </span>
              </div>
            </div>
          </div>

          <div className="psd-print-info">
            <span className="psd-print-card-icon" aria-hidden="true">
              <InfoClipboardIcon />
            </span>
            <div className="psd-print-info-content">
              <div className="psd-print-card-title">{t("usi.infoTitle")}</div>
              <div className="psd-print-info-text">{t("psd.print.infoText")}</div>
            </div>
          </div>
        </div>

        <div className="plan-print-other-header-divider psd-print-slogan" aria-hidden="true">
          <span className="plan-print-other-header-divider-line" />
          <span className="plan-print-other-header-divider-diamond" />
          <span className="plan-print-other-diagnostics-heading">
            {t("usi.slogan")}
          </span>
          <span className="plan-print-other-header-divider-diamond" />
          <span className="plan-print-other-header-divider-line" />
        </div>
      </div>
    </>
  );
}

function PatientSmartDiagnosticsPrint() {
  const { t } = useTranslation("patient");
  const { patientId, planId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({ ...SMART_DIAGNOSTICS_FORM_DEFAULT });
  const [appointments, setAppointments] = useState(null);
  const [docMeta, setDocMeta] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");

      try {
        const [appointmentsData, docsListData] = await Promise.all([
          GetData({
            module: "patient",
            block: "list_appointments",
            id_patient: patientId,
          }),
          GetData({
            module: "patient",
            block: "patient_docs",
            id_patient: patientId,
            action: "list",
          }),
        ]);

        setAppointments(appointmentsData);

        const smartDoc = findLatestSmartDiagnosticsDoc(docsListData?.result);

        if (smartDoc?.id) {
          const smartDocData = await GetData({
            module: "patient",
            block: "patient_docs",
            action: "get",
            id_patient: patientId,
            id: smartDoc.id,
          });
          setFormData(mapSmartDiagnosticsDocData(smartDocData?.result?.data));
          setDocMeta(smartDocData?.result || smartDoc);
        } else {
          setFormData({ ...SMART_DIAGNOSTICS_FORM_DEFAULT });
          setDocMeta(null);
        }
      } catch (err) {
        setError(err?.message || t("psd.loadFailed"));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [patientId, planId, t]);

  if (loading) return <div className="plan-print-loading">{t("plan.loading")}</div>;
  if (error) return <div className="plan-print-error">{t("usi.errorPrefix", { message: error })}</div>;

  return (
    <div className="patient-plan-print-page patient-plan-print-other patient-smart-diagnostics-print">
      <div className="plan-print-controls no-print">
        <Button
          icon="print"
          onClick={() => window.print()}
          variant="secondary"
        >
          {t("plan.buttons.print")}
        </Button>
        <Button
          icon="back"
          onClick={() => window.history.back()}
          variant="primary"
        >
          {t("docs.buttons.backToList")}
        </Button>
      </div>

      <SmartDiagnosticsPrintSheet
        formData={formData}
        appointments={appointments}
        docMeta={docMeta}
      />
    </div>
  );
}

export default PatientSmartDiagnosticsPrint;
